/**
 * Today (build spec, "To-dos": what do I owe today): the day's meetings with their outcome,
 * to-dos due, and the show rate. Pure functions over activities (Follow-up tasks).
 */
import type { Identifier } from "ra-core";

import type { ZcrmActivity } from "../providers/zinspector/activities";

const DAY_MS = 24 * 60 * 60 * 1000;
/** The show rate looks back this many days. */
export const SHOW_RATE_DAYS = 30;

export const startOfDay = (now: Date) =>
  new Date(now.getFullYear(), now.getMonth(), now.getDate());

const isMine = (a: ZcrmActivity, ownerId: Identifier | null) =>
  ownerId == null || String(a.sales_id) === String(ownerId);

const isMeeting = (a: ZcrmActivity) =>
  a.kind === "Meeting" || a.kind === "Call";

export type MeetingState = "upcoming" | "ask_outcome" | "held" | "no_show";

export const meetingState = (
  meeting: ZcrmActivity,
  now = new Date(),
): MeetingState => {
  if (meeting.is_canceled) return "no_show";
  if (meeting.is_done) return "held";
  return meeting.starts_at && Date.parse(meeting.starts_at) <= now.getTime()
    ? "ask_outcome"
    : "upcoming";
};

/** Calls and meetings starting today, earliest first. */
export const meetingsToday = (
  activities: ZcrmActivity[],
  ownerId: Identifier | null,
  now = new Date(),
): ZcrmActivity[] => {
  const start = startOfDay(now).getTime();
  const end = start + DAY_MS;
  return activities
    .filter((a) => isMeeting(a) && isMine(a, ownerId) && a.starts_at)
    .filter((a) => {
      const t = Date.parse(a.starts_at as string);
      return t >= start && t < end;
    })
    .sort((a, b) => (a.starts_at ?? "").localeCompare(b.starts_at ?? ""));
};

/** Earlier no-shows with the same person (or, with no contact, the same account). */
export const earlierNoShows = (
  activities: ZcrmActivity[],
  meeting: ZcrmActivity,
): number =>
  activities.filter(
    (a) =>
      a.id !== meeting.id &&
      a.is_canceled &&
      isMeeting(a) &&
      (meeting.contact_id
        ? String(a.contact_id) === String(meeting.contact_id)
        : !!meeting.company_id &&
          String(a.company_id) === String(meeting.company_id)) &&
      (a.starts_at ?? "") < (meeting.starts_at ?? ""),
  ).length;

/** Open to-dos due by the end of today (overdue ones included), earliest first. */
export const toDosDue = (
  activities: ZcrmActivity[],
  ownerId: Identifier | null,
  now = new Date(),
): (ZcrmActivity & { is_overdue: boolean })[] => {
  const today = startOfDay(now).getTime();
  const end = today + DAY_MS;
  return activities
    .filter(
      (a) =>
        a.kind === "To-do" &&
        !a.is_done &&
        !a.is_canceled &&
        isMine(a, ownerId) &&
        Date.parse(a.ends_at ?? a.starts_at ?? "") < end,
    )
    .map((a) => ({
      ...a,
      is_overdue: Date.parse(a.ends_at ?? a.starts_at ?? "") < today,
    }))
    .sort((a, b) =>
      (a.ends_at ?? a.starts_at ?? "").localeCompare(
        b.ends_at ?? b.starts_at ?? "",
      ),
    );
};

/** Held meetings out of those that had an outcome, over the last 30 days; null with none. */
export const showRate = (
  activities: ZcrmActivity[],
  ownerId: Identifier | null,
  now = new Date(),
): { rate: number; held: number; noShows: number } | null => {
  const since = now.getTime() - SHOW_RATE_DAYS * DAY_MS;
  const past = activities.filter(
    (a) =>
      isMeeting(a) &&
      isMine(a, ownerId) &&
      a.starts_at &&
      Date.parse(a.starts_at) >= since &&
      Date.parse(a.starts_at) <= now.getTime(),
  );
  const held = past.filter((a) => a.is_done).length;
  const noShows = past.filter((a) => a.is_canceled).length;
  return held + noShows === 0
    ? null
    : { rate: held / (held + noShows), held, noShows };
};

export const PRE_CALL_TEMPLATES = [
  {
    id: "on_time",
    label: "On time",
    text: "Hi {first}, it's {host} from {company}. Calling you at {time} as planned. Talk soon.",
  },
  {
    id: "late",
    label: "Running 5 min late",
    text: "Hi {first}, it's {host} from {company}. I'm running 5 minutes late for our {time} call, sorry. Talk soon.",
  },
  {
    id: "video",
    label: "Video link",
    text: "Hi {first}, it's {host} from {company}. Here's the link for our {time} call: {link}",
  },
] as const;

export const fillTemplate = (
  text: string,
  values: Record<string, string>,
): string => text.replace(/\{(\w+)\}/g, (_, key: string) => values[key] ?? "");

/** Opens the phone's messaging app with the text ready (the rep's own number sends it). */
export const smsLink = (phone: string, text: string) =>
  `sms:${phone.replace(/[^\d+]/g, "")}?body=${encodeURIComponent(text)}`;
